import { Model } from 'dva';
import { routerRedux } from 'dva/router';
import smoothscroll from 'smoothscroll-polyfill';
import { delay } from 'dva/saga';
import { configure } from '@mos-utils/drafts';

import { DEFAULT_FETCH_OPTIONS, serviceID, xCallerId } from './auth';
import fetchHelper from '../helpers/fetchHelper';

import { getDraftSaveDate, sortDates } from '../helpers/dateHelpers';

import parseServerValidationError from '../helpers/parseServerValidationError';
import { hasErrors, validators } from '../validators';

import { errorSubmitModalOpen } from './modals';
import { SubmitRequestError } from '../errors/submit';
import { ERROR_DATE } from '../constants/errors';
import { MARRIAGE_INFO_FIELD_NAME } from '../constants/fieldNames';
import { validateChildDocumentIssuedDate } from '../validators/child';
import {
  resetFormClick,
  formError,
  sendFormPageStatus,
  sendFormSuccess,
  sendStats,
  sendForm,
} from '../helpers/stats';

import { showNotification } from './notifications';
import { transformDataFromDraft } from '../helpers/transformDataFromDraft';

import {
  initialReadyNamespaces,
  NamespacesReadyToSendStats,
} from '../helpers/initialState';
import { FORM } from '../constants/modelNames';
import {
  getAgreements,
  isAllChildrenBornInCurrentMarriage,
  submitDraftSelector,
  submitSelector,
} from '../selectors/submit';
import { createSelector } from 'reselect';
import { getNotHiddenChildren } from '../selectors/children';
import { sendCMAFormSubmit, sendCMATechError } from '../helpers/CMAstats';

smoothscroll.polyfill();

const DRAFT_ID = 'mos.auto-save';

type Unwrap<T> = T extends PromiseLike<infer Data> ? Data : T;

const drafts = configure({
  apiBase: '/api/ums/v1/drafts',
  serviceID,
  xCallerId,
});

export interface AddressFieldsForSubmit {
  region: string;
  area: string;
  district: string;
  street: string;
  house: string;
  building: string;
  structure: string;
  flat: string;
  bti_building_code: string;
  omk_street_code: string;
  omk_district_code: string;
  omk_area_code: string;
  fias_id: string;
  is_serve_by_eirc: boolean;
  is_manual_input: boolean;
  is_registered_in_moscow?: boolean;
}

export interface MarriageForSubmit {
  date_of_marriage: string;
  act_issued_by: string;
  act_number: string;
}

export type SpouseAddressForSubmit =
  | AddressFieldsForSubmit
  | Partial<AddressFieldsForSubmit>;
export type ChildAddressForSubmit =
  | AddressFieldsForSubmit
  | Partial<AddressFieldsForSubmit>
  | null;

const saveDraftSelector = createSelector(
  submitDraftSelector,
  getAgreements,
  (formData, { warnedAboutResponsibility, consentWithTerms }) => {
    return {
      ...formData,
      warned_about_responsibility: warnedAboutResponsibility.checked,
      consent_with_terms: consentWithTerms.checked,
    };
  }
);

export interface SubmitModelProps {
  savedTime: string;
  draftTime: string;
  recoveredFromDraft: boolean;
  draftDownloadError: boolean;
  serviceNumber: string;
  draftId: string;
  readyNamespaces: NamespacesReadyToSendStats;
}

const initialSubmitState: SubmitModelProps = {
  savedTime: '',
  draftTime: '',
  recoveredFromDraft: false,
  draftDownloadError: false,
  serviceNumber: '',
  draftId: '',
  readyNamespaces: initialReadyNamespaces,
};

const submitModel: Model = {
  namespace: FORM,
  state: initialSubmitState,
  reducers: {
    'set/error'(state, { payload }) {
      return { ...state, ...payload };
    },
    'set/serviceNumber'(state, { payload }) {
      state.serviceNumber = payload;
      return state;
    },
    'set/draftId'(state, { payload }) {
      state.draftId = payload;
      return state;
    },
    setDraftInfo(state, { payload }) {
      return { ...state, ...payload };
    },
    reset(state) {
      sendStats(resetFormClick());
      state = initialSubmitState;
      Object.keys(state.readyNamespaces).forEach((key) => {
        if (state.readyNamespaces.hasOwnProperty(key)) {
          state.readyNamespaces[key] = false;
        }
      });
      return state;
    },
    'reset/readyNamespaces'(state) {
      Object.keys(state.readyNamespaces).forEach((key) => {
        if (state.readyNamespaces.hasOwnProperty(key)) {
          state.readyNamespaces[key] = false;
        }
      });
      return state;
    },
    'set/readyNamespaces'(state) {
      Object.keys(state.readyNamespaces).forEach((key) => {
        if (state.readyNamespaces.hasOwnProperty(key)) {
          state.readyNamespaces[key] = true;
        }
      });
      return state;
    },
    'set/readyNamespace'(state, { payload }) {
      state.readyNamespaces[payload] = true;
      return state;
    },
  },
  effects: {
    *'validate/all'({}, { select, put }) {
      const state = yield select((state) => state);
      const formValidationResult = parseServerValidationError(
        validators.validateForm(state)
      );
      const children = yield select(getNotHiddenChildren);
      const childrenBirthStatus = yield select(
        isAllChildrenBornInCurrentMarriage
      );

      sendStats(sendForm(children.length, childrenBirthStatus));
      const formHasErrors = hasErrors(formValidationResult);
      if (formHasErrors) {
        yield put({
          type: 'set/error',
          payload: formValidationResult,
        });
        yield put({ type: 'scrollToError' });
      } else {
        yield put({ type: 'submit' });
      }
    },
    *scrollToError({ payload = '' }) {
      setTimeout(() => {
        // Плохое быстрое решение. Нужно придумать более нажёжный механизм
        const errorSelectors = [
          `${payload} .TextFieldWrapper_hasError`,
          `${payload} .Message.Block_danger`,
          `${payload} .Radio_error`,
          `${payload} .Checkbox_error`,
        ];
        if (!payload) {
          errorSelectors.push('.Card_error');
        }
        const error = document.querySelector(errorSelectors.join(','));
        if (error) {
          error.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, 0);
    },
    *submit(_, { select, put, call }) {
      const submitData = yield select(submitSelector);

      try {
        const fetchData = {
          ...DEFAULT_FETCH_OPTIONS,
          method: 'POST',
          headers: new Headers({
            ...DEFAULT_FETCH_OPTIONS.headers,
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(submitData),
        };

        const response = yield call(
          fetchHelper,
          '/api/ums/v1/application',
          fetchData
        );
        const { data } = yield call([response, 'json']);

        const { success, service_number: serviceNumber } = data;
        if (success && serviceNumber) {
          sendStats(sendFormSuccess(serviceNumber, submitData.children.length));
          sendCMAFormSubmit();
          yield put({ type: 'set/serviceNumber', payload: serviceNumber });
          yield put(
            routerRedux.push({
              pathname: '/success/',
              state: {
                status: 'success',
              },
            })
          );
          return;
        }
        sendCMATechError('final');
        throw new SubmitRequestError('Заявление не удалось отправить');
      } catch (error) {
        sendStats(formError('Заявление не удалось отправить'));
        sendCMATechError('final');
        yield put(errorSubmitModalOpen(true));
      }
    },
    *'find/relativeFields/forSetError'({ payload }, { put, select }) {
      const { field, error } = payload;

      if (error === ERROR_DATE.BIRTHDATE_EARLY_MARRIAGE_DATE) {
        if (field === 'spouse.birthdate') {
          return yield put({
            type: `applicant/set/error/relativeFields`,
            payload: {
              fields: [MARRIAGE_INFO_FIELD_NAME.dateOfMarriage],
              error: true,
            },
          });
        }
        if (field === 'applicant.dateOfMarriage') {
          return yield put({
            type: `spouse/set/error/relativeFields`,
            payload: {
              fields: ['birthdate'],
              error: true,
            },
          });
        }
      }

      if (error === ERROR_DATE.DOCUMENT_DATE_EARLY_BIRTHDATE) {
        if (field === 'spouse.birthdate') {
          return yield put({
            type: `spouse/set/dul/issueDate/error`,
          });
        }
        if (field === 'spouse.dul.issueDate') {
          return yield put({
            type: `spouse/set/error/relativeFields`,
            payload: {
              fields: ['birthdate'],
              error: true,
            },
          });
        }
        if (
          ['child.dul.issueDate', 'child.birthCertificate.issueDate'].includes(
            field
          )
        ) {
          return yield put({
            type: `child/set/error/relativeFields`,
            payload: {
              fields: ['birthdate'],
              error: true,
            },
          });
        }
        if (field === 'child.birthdate') {
          const dulError = yield select(validateChildDocumentIssuedDate);
          return yield put({
            type: `child/set/${
              dulError === error ? 'dul' : 'bc'
            }/issueDate/error`,
          });
        }
      }

      if (error === ERROR_DATE.DOCUMENT_DATE_EARLY_THAN_14) {
        if (field === 'spouse.dul.issueDate') {
          return yield put({
            type: `spouse/set/error/relativeFields`,
            payload: {
              fields: ['birthdate'],
              error: true,
            },
          });
        }
        if (field === 'spouse.birthdate') {
          return yield put({
            type: `spouse/set/dul/issueDate/error`,
          });
        }
        if (field === 'child.dul.issueDate') {
          return yield put({
            type: `child/set/error/relativeFields`,
            payload: {
              fields: ['birthdate'],
              error: true,
            },
          });
        }
        if (field === 'child.birthdate') {
          return yield put({
            type: `child/set/dul/issueDate/error`,
          });
        }
      }

      if (error === ERROR_DATE.BIRTHDATE_EARLY_PATERNITY_DATE) {
        if (field === 'child.secondParent.paternityRegisterDate') {
          return yield put({
            type: `child/set/error/relativeFields`,
            payload: {
              fields: ['birthdate'],
              error: true,
            },
          });
        }
        if (field === 'child.birthdate') {
          return yield put({
            type: `child/secondParent/set/error/relativeFields`,
            payload: {
              fields: ['paternityRegisterDate'],
              error: true,
            },
          });
        }
      }

      if (error === ERROR_DATE.DEATHDATE_EARLY_BIRTHDATE) {
        if (field === 'child.secondParent.birthdate') {
          return yield put({
            type: `child/secondParent/set/error/relativeFields`,
            payload: {
              fields: ['deathdate'],
              error: true,
            },
          });
        }
        if (field === 'child.secondParent.deathdate') {
          return yield put({
            type: `child/secondParent/set/error/relativeFields`,
            payload: {
              fields: ['birthdate'],
              error: true,
            },
          });
        }
      }

      if (error === ERROR_DATE.DIVORCE_DATE_EARLY_MARRIAGE) {
        if (field === 'child.secondParent.dateOfMarriage') {
          return yield put({
            type: `child/secondParent/set/error/relativeFields`,
            payload: {
              fields: ['dateOfDivorce'],
              error: true,
            },
          });
        }
        if (field === 'child.secondParent.dateOfDivorce') {
          return yield put({
            type: `child/secondParent/set/error/relativeFields`,
            payload: {
              fields: ['dateOfMarriage'],
              error: true,
            },
          });
        }
      }

      if (error === ERROR_DATE.PASSPORT_IS_EXPIRED) {
        if (field === 'spouse.dul.issueDate') {
          return yield put({
            type: `spouse/set/error/relativeFields`,
            payload: {
              fields: ['birthdate'],
              error: true,
            },
          });
        }
        if (field === 'spouse.birthdate') {
          return yield put({
            type: `spouse/set/dul/issueDate/error`,
          });
        }
        if (field === 'child.dul.issueDate') {
          return yield put({
            type: `child/set/error/relativeFields`,
            payload: {
              fields: ['birthdate'],
              error: true,
            },
          });
        }
        if (field === 'child.birthdate') {
          return yield put({
            type: `child/set/dul/issueDate/error`,
          });
        }
      }
    },
    *saveDraft(_, { select, call, put }) {
      const submitData = yield select(saveDraftSelector);

      try {
        yield call([drafts, 'save'], DRAFT_ID, submitData);
        const responseDate = new Date().toString();
        const savedTime = getDraftSaveDate(responseDate);

        yield put(showNotification({ type: 'saveDraftSuccess' }));
        yield put({
          type: 'setDraftInfo',
          payload: {
            savedTime,
            draftId: DRAFT_ID,
          },
        });
      } catch (e) {
        sendCMATechError('step');
      }
    },
    *'delete/draft'(_, { select, call }) {
      const draftId = yield select((state) => state.form.draftId);

      try {
        yield call([drafts, 'delete'], draftId);
      } catch (e) {}
    },
    *getDraft(_, { put, call }) {
      yield put({
        type: 'setDraftInfo',
        payload: {
          draftDownloadError: false,
        },
      });

      try {
        const response = yield call([drafts, 'fetchList']);

        if (response.code !== 'SUCCESS') {
          throw new Error('Не удалось загрузить черновики');
        }
        const {
          list,
        }: Unwrap<ReturnType<typeof drafts['fetchList']>> = response;

        if (!list.length) {
          sendStats(sendFormPageStatus('Новое'));
          return;
        }
        const { version: draftId, created_at: date } = list.sort((a, b) =>
          sortDates(a.created_at, b.created_at)
        )[0];

        const { data }: Unwrap<ReturnType<typeof drafts['load']>> = yield call(
          [drafts, 'load'],
          draftId
        );

        sendStats(sendFormPageStatus('Загружено из черновика'));
        yield put({
          type: 'setDraftInfo',
          payload: {
            draftTime: getDraftSaveDate(date),
            draftId,
            recoveredFromDraft: true,
          },
        });
        yield put({
          type: 'reset/readyNamespaces',
        });
        yield put({
          type: 'form/set/draftData',
          payload: transformDataFromDraft(data),
        });
      } catch (e) {
        sendCMATechError('step');
        yield put({
          type: 'setDraftInfo',
          payload: {
            draftDownloadError: true,
          },
        });
      }
    },
    *reset(_, { put }) {
      yield delay(0); // delay for dispatch event on next tick
      yield put({
        type: 'set/readyNamespaces',
      });
      yield put({
        type: 'setDraftInfo',
        payload: {
          draftDownloadError: false,
        },
      });
    },
  },
};
export default submitModel;
